import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Actions } from '~/eds/src/components/Actions/Actions';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { Dropdown } from '~/eds/src/components/Dropdown/Dropdown';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { useToggle } from '~/eds/src/hooks/useToggle';
import { api } from '~/redux/api/createApi';
import { GuidelineWithId } from '~/redux/api/types/playbooks/guidelines';

type Props = {
  // List of UUIDs to ignore in the selection list
  selectedIds?: string[];
  onChange: (guidelines: GuidelineWithId[]) => void;
};

export const GuidelinesDropdown = ({ selectedIds, onChange }: Props) => {
  const [isVisible, _toggle, show, hide] = useToggle();
  const intl = useIntl();
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [search, setSearch] = useState<string>('');

  const { data: guidelinesResponse = { data: [] }, isFetching } =
    api.endpoints.getGuidelines.useQuery(search);

  const trigger = (
    <Button
      isFullWidth
      variant={isVisible ? 'secondary-active' : 'secondary'}
      onClick={show}
      text={intl.formatMessage({
        id: 'Guidelines.addExistingGuidelineButton',
        defaultMessage: 'Add Existing Guideline',
        description: 'Button label to add a new guideline',
      })}
      icon="plus"
      iconPosition="left"
    />
  );

  const options = useMemo(
    () =>
      guidelinesResponse?.data
        .filter(
          (guideline) => !selectedIds?.includes(guideline.attributes.uuid),
        )
        .map((guideline) => ({
          label: guideline.attributes.name,
          value: guideline.attributes.uuid,
        })),
    [guidelinesResponse, selectedIds],
  );

  return (
    <Dropdown
      isVisible={isVisible}
      trigger={trigger}
      triggerWidth="100%"
      triggerMode="triggerWidth"
      onClickOutside={hide}
    >
      {isVisible && (
        <Layout direction="column" spacing={2} p={2}>
          <Select
            enableSelectAll={false}
            isLoading={isFetching}
            isEmbedded
            width="100%"
            isMulti
            onSearch={setSearch}
            options={options}
            name="Guidelines"
            value={selectedOptions}
            onChange={(value) => setSelectedOptions(value ?? [])}
          />
          <Box borderTop="border.divider">
            <Actions
              justify="right"
              actions={[
                {
                  level: 'tertiary',
                  text: intl.formatMessage({
                    id: 'Guidelines.cancelButton',
                    defaultMessage: 'Cancel',
                    description: 'Button label to cancel adding guidelines',
                  }),
                  onClick: hide,
                },
                {
                  level: 'action',
                  text: intl.formatMessage({
                    id: 'Guidelines.addSelectedGuidelinesButton',
                    defaultMessage: 'Apply',
                    description: 'Button label to add selected guidelines',
                  }),
                  onClick: () => {
                    const selectedGuidelines = guidelinesResponse?.data
                      .filter((guideline) =>
                        selectedOptions.includes(guideline.attributes.uuid),
                      )
                      .map((guideline) => guideline.attributes);
                    onChange(selectedGuidelines);
                    hide();
                  },
                },
              ]}
            />
          </Box>
        </Layout>
      )}
    </Dropdown>
  );
};
